// ============================================================================
// /cattle/weighins list metrics — Days since last weigh-in, and Avg ADG
// ============================================================================
//
// LIST-ONLY. The session record page keeps its own figures and its own
// behaviour; nothing here is shared with it and no parity is claimed. This
// derives two columns from the payload `cattle.weighins.list` already returns
// (every cattle session plus every cattle weigh-in grouped by session), so it
// adds no query, RPC, directory read or per-row request.
//
// Days since last weigh-in — calendar days back to the newest prior COMPLETE
// same-herd session, ordered by date then started_at then id.
//
// Avg ADG — for each tag in this session, its latest usable weight in a prior
// COMPLETE same-herd session; per-tag ADG is (current - prior) / that tag's own
// calendar-day gap; the figure is the mean of the per-tag values that exist.
// A tag with no prior, no usable weight on either side, or a zero-day gap is
// EXCLUDED, never counted as zero.
//
// First session, unparseable dates and zero-day gaps all render an em dash.
// Negative gain is preserved.
//
// ONE ROLLING PASS. The sessions are sorted once, then walked ascending
// carrying a tag -> prior map and a last-complete pointer per herd. Each
// session reads that state and only then merges its own entries into it, which
// is what makes the state mean "strictly earlier" without re-filtering the
// history per row: O(S log S + E), each entry visited a constant number of
// times. Nothing here mutates its arguments.
// ============================================================================

/** A `weigh_in_sessions` row, reduced to the columns these metrics read. */
export type CattleWeighInSessionLike = {
  id?: string | null;
  date?: string | null;
  started_at?: string | null;
  status?: string | null;
  herd?: string | null;
};

/** A `weigh_ins` row, reduced to the columns these metrics read. */
export type CattleWeighInEntryLike = {
  id?: string | null;
  tag?: string | null;
  weight?: number | string | null;
  entered_at?: string | null;
};

export type CattleWeighInListMetrics = {
  daysSinceLastWeighIn: number | null;
  avgAdgLbsPerDay: number | null;
  matchedAnimalCount: number;
  animalCount: number;
};

const COMPLETE_STATUS = 'complete';
const MS_PER_DAY = 86400000;

export const EMPTY_CATTLE_WEIGH_IN_LIST_METRICS: CattleWeighInListMetrics = Object.freeze({
  daysSinceLastWeighIn: null,
  avgAdgLbsPerDay: null,
  matchedAnimalCount: 0,
  animalCount: 0,
});

/**
 * Whole days between two `YYYY-MM-DD` dates, or null when either is absent or
 * unparseable or the difference is under a day. Both are read at noon so a
 * daylight-saving boundary inside the span cannot move the result.
 */
export function calendarDayGap(
  priorDate: string | null | undefined,
  currentDate: string | null | undefined,
): number | null {
  if (!priorDate || !currentDate) return null;
  const prior = new Date(String(priorDate) + 'T12:00:00');
  const current = new Date(String(currentDate) + 'T12:00:00');
  const days = Math.round((current.getTime() - prior.getTime()) / MS_PER_DAY);
  return Number.isFinite(days) && days >= 1 ? days : null;
}

/** A genuinely numeric weight, or null. Never 0-by-fallback. */
function usableWeight(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

/** date, then started_at, then id — total, so the order never depends on input order. */
function compareSessions(a: CattleWeighInSessionLike, b: CattleWeighInSessionLike): number {
  const byDate = String(a.date ?? '').localeCompare(String(b.date ?? ''));
  if (byDate !== 0) return byDate;
  const byStart = String(a.started_at ?? '').localeCompare(String(b.started_at ?? ''));
  if (byStart !== 0) return byStart;
  return String(a.id ?? '').localeCompare(String(b.id ?? ''));
}

/** entered_at, then id — the newest usable row wins a duplicate tag. */
function isNewerEntry(candidate: CattleWeighInEntryLike, held: CattleWeighInEntryLike): boolean {
  const byEntered = String(candidate.entered_at ?? '').localeCompare(String(held.entered_at ?? ''));
  if (byEntered !== 0) return byEntered > 0;
  return String(candidate.id ?? '').localeCompare(String(held.id ?? '')) > 0;
}

/** One usable weight per tag: the newest by (entered_at, id). Linear, no sort. */
function collapseByTag(
  rows: readonly CattleWeighInEntryLike[],
): Map<string, {entry: CattleWeighInEntryLike; weight: number}> {
  const byTag = new Map<string, {entry: CattleWeighInEntryLike; weight: number}>();
  for (const entry of rows) {
    if (!entry || !entry.tag) continue;
    const weight = usableWeight(entry.weight);
    if (weight === null) continue;
    const key = String(entry.tag);
    const held = byTag.get(key);
    if (!held || isNewerEntry(entry, held.entry)) byTag.set(key, {entry, weight});
  }
  return byTag;
}

type PriorWeight = {weight: number; date: string | null | undefined};

/**
 * Metrics for every session, keyed by session id. A session with no usable
 * history still gets an entry, so a caller never has to distinguish "not
 * computed" from "nothing to compute".
 */
export function buildCattleWeighInListMetrics(
  sessions: readonly CattleWeighInSessionLike[] | null | undefined,
  entriesBySession: Readonly<Record<string, readonly CattleWeighInEntryLike[]>> | null | undefined,
): Map<string, CattleWeighInListMetrics> {
  const rows = Array.isArray(sessions) ? sessions : [];
  const entries = entriesBySession || {};
  // Copy before sorting: the array belongs to the route resource.
  const ascending = rows.slice().sort(compareSessions);

  const rollingByHerd = new Map<string, Map<string, PriorWeight>>();
  const lastCompleteByHerd = new Map<string, CattleWeighInSessionLike>();
  const metrics = new Map<string, CattleWeighInListMetrics>();

  for (const session of ascending) {
    if (!session) continue;
    const sessionId = String(session.id ?? '');
    const herdKey = String(session.herd ?? '');
    const currentByTag = collapseByTag(entries[sessionId] || []);

    let rolling = rollingByHerd.get(herdKey);
    if (!rolling) {
      rolling = new Map<string, PriorWeight>();
      rollingByHerd.set(herdKey, rolling);
    }
    const priorSession = lastCompleteByHerd.get(herdKey) || null;

    const adgs: number[] = [];
    for (const [tag, current] of currentByTag) {
      const prior = rolling.get(tag);
      if (!prior) continue;
      const days = calendarDayGap(prior.date, session.date);
      if (days === null) continue;
      const adg = (current.weight - prior.weight) / days;
      if (Number.isFinite(adg)) adgs.push(adg);
    }

    metrics.set(sessionId, {
      daysSinceLastWeighIn: priorSession ? calendarDayGap(priorSession.date, session.date) : null,
      avgAdgLbsPerDay: adgs.length > 0 ? adgs.reduce((sum, value) => sum + value, 0) / adgs.length : null,
      matchedAnimalCount: adgs.length,
      animalCount: currentByTag.size,
    });

    // MERGE LAST: only a complete session becomes history, and only after its
    // own figures were taken against the state that excludes it.
    if (session.status === COMPLETE_STATUS) {
      for (const [tag, current] of currentByTag) rolling.set(tag, {weight: current.weight, date: session.date});
      lastCompleteByHerd.set(herdKey, session);
    }
  }

  return metrics;
}

/** The metrics for one session, or the empty ones. */
export function cattleWeighInListMetrics(
  metrics: ReadonlyMap<string, CattleWeighInListMetrics> | null | undefined,
  sessionId: unknown,
): CattleWeighInListMetrics {
  if (!metrics) return EMPTY_CATTLE_WEIGH_IN_LIST_METRICS;
  return metrics.get(String(sessionId ?? '')) || EMPTY_CATTLE_WEIGH_IN_LIST_METRICS;
}

/** `27 days` / `1 day`, or null when the column should render an em dash. */
export function formatDaysSinceLastWeighIn(days: number | null | undefined): string | null {
  if (days === null || days === undefined) return null;
  return days + (days === 1 ? ' day' : ' days');
}

/** `+0.74 lb/d` — always signed, two decimals, or null for an em dash. */
export function formatAvgAdg(value: number | null | undefined): string | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return (value >= 0 ? '+' : '') + value.toFixed(2) + ' lb/d';
}

/** The ok/danger/muted ink an ADG carries. Negative gain stays visible. */
export function avgAdgTone(value: number | null | undefined): 'ok' | 'danger' | 'muted' {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'muted';
  if (value > 0) return 'ok';
  if (value < 0) return 'danger';
  return 'muted';
}

/** How much of the session the figure speaks for. */
export function avgAdgProvenance(metrics: CattleWeighInListMetrics | null | undefined): string {
  const matched = metrics ? metrics.matchedAnimalCount : 0;
  const total = metrics ? metrics.animalCount : 0;
  return `${matched} of ${total} ${total === 1 ? 'animal' : 'animals'} matched a prior weigh-in`;
}
